/// <reference path="../typings/linq.d.ts" />
/// <reference path="AppKitchen.Controls.Popups.Notification.ts" />
/// <reference path="AppKitchen.Data.Export.ts" />

module AppKitchen {

    // BrowserHelper must be always on Top
    export module BrowserHelper {

        export function triggerWindowResizeEvent() {
            var evt = window.document.createEvent('UIEvents');
            evt.initUIEvent('resize', true, false, window, 0);
            window.dispatchEvent(evt);
        }

        // ReSharper disable once InconsistentNaming
        export function isIE(): boolean {
            var userAgent = AppKitchen.BrowserHelper.getUserAgent().toUpperCase();
            return userAgent.indexOf("MSIE") !== -1 || !!navigator.userAgent.match(/TRIDENT\/7\./);
        }

        // ReSharper disable once InconsistentNaming
        export function IEVersion(): number {
            var userAgent = AppKitchen.BrowserHelper.getUserAgent().toUpperCase();
            var index = userAgent.indexOf("MSIE");
            if (index !== -1) {
                return parseInt(userAgent.substring(index + 5, Math.max(2, userAgent.indexOf(".", index))));
            } else if (!!userAgent.match(/TRIDENT\/7\./)) {
                return 11;
            }

            return undefined;
        }

        export function getUserAgent() {
            return window.navigator.userAgent;
        }

        function throwStartupError(userFriendlyMessage: string, debugMessage: string) {
            // do not user jQuery! intended to work with old browsers
            var errorInfo = document.createElement('div');
            errorInfo.className = "a-startup-error";
            errorInfo.innerHTML = 'App can not be started due to the following reason:<br>' + userFriendlyMessage;
            document.body.appendChild(errorInfo);
            throw debugMessage;
        }

        export function checkBrowser() {
            if (AppKitchen.BrowserHelper.isIE()) {
                var ieVersion = AppKitchen.BrowserHelper.IEVersion();
                if (ieVersion && ieVersion < 10) {
                    throwStartupError("Incompatible browser! App requires Internet Explorer 10 or newer.", "Incompatible IE version detected " + ieVersion + ", minimum required version is 10.");
                }
            }
        }

        // check browser version immediately and throw error if incompatible
        checkBrowser();

        // ReSharper disable once InconsistentNaming
        export interface BrowserWindowOptions {
            addressbar?: boolean;
            toolbar?: boolean;
            menubar?: boolean;
            statusbar?: boolean;
            resizable?: boolean;
            width?: number;
            height?: number;
            position?: { top: number, left: number };
        }

        export function openInWindow(url: string, minimal?: boolean, options?: BrowserWindowOptions): Window {
            options = AppKitchen.OptionsHelper.merge(options, {
                addressbar: true,
                menubar: !minimal,
                toolbar: !minimal,
                statusbar: !minimal,
                resizable: true,
                width: undefined,
                height: undefined,
                position: undefined
            });

            var features = [];
            if (options.addressbar != null) features.push("location=" + (options.addressbar ? "yes" : "no"));
            if (options.menubar != null) features.push("menubar=" + (options.menubar ? "yes" : "no"));
            if (options.toolbar != null) features.push("toolbar=" + (options.toolbar ? "yes" : "no"));
            if (options.statusbar != null) features.push("status=" + (options.statusbar ? "yes" : "no"));
            if (options.resizable != null) features.push("resizable=" + (options.resizable ? "yes" : "no"));
            if (options.width != null) features.push("width=" + options.width);
            if (options.height != null) features.push("height=" + options.height);
            if (options.position != null) {
                features.push("left=" + options.position.left);
                features.push("top=" + options.position.top);
            }

            var openedWindow = window.open(url, "_blank", features.join(","));
            openedWindow.focus();

            return openedWindow;
        }

        // ReSharper disable once InconsistentNaming
        export interface RedirectOptions {
            refresh?: boolean;
            push?: boolean;
        }

        export function setUrl(url: string, options?: RedirectOptions) {
            options = AppKitchen.OptionsHelper.merge(options, {
                refresh: true,
                push: true
            });

            if (!options.refresh && history.replaceState && history.pushState) {
                if (options.push) {
                    window.history.pushState({ path: url }, null, url);
                } else {
                    window.history.replaceState({ path: url }, null, url);
                }
            } else {
                if (window.location.href === url) {
                    window.location.reload(true);
                }
                if (options.push) {
                    window.location.href = url;
                } else {
                    window.location.replace(url);
                    window.setTimeout(() => window.location.reload(true), 1000); // reloads if replace did not (when using #route)
                }
            }
        }

        export function getUrl(): string {
            return window.location.href;
        }

        export module UrlQuery {
            export function getParameters(): { [key: string]: any } {
                var queryParams = {};
                var url = AppKitchen.BrowserHelper.getUrl() || "";
                var queryString = (url.indexOf("?") !== -1) ? url.split("?")[1] : null;
                if (queryString) {
                    var vars = queryString.split("&");
                    for (var i = 0; i < vars.length; i++) {
                        var pair = vars[i].split("=");
                        if (typeof queryParams[pair[0]] === "undefined") {
                            queryParams[pair[0]] = decodeURIComponent(pair[1]);
                        } else if (typeof queryParams[pair[0]] === "string") {
                            var arr = [queryParams[pair[0]], decodeURIComponent(pair[1])];
                            queryParams[pair[0]] = arr;
                        } else {
                            queryParams[pair[0]].push(decodeURIComponent(pair[1]));
                        }
                    }
                }

                return queryParams;
            }

            export function getParameter(key: string): string {
                var parameters = UrlQuery.getParameters();
                if (parameters) {
                    return parameters[key];
                }
                return undefined;
            }

            export function setParameter(key: string, value: string, options?: RedirectOptions) {
                options = AppKitchen.OptionsHelper.merge(options, {
                    push: false,
                    refresh: false
                });

                var url = AppKitchen.BrowserHelper.getUrl();
                var baseUrl = url.split("?")[0];

                var keyFound = false;
                var parameters = [];

                var queryString = (url.indexOf("?") !== -1) ? url.split("?")[1] : null;
                if (queryString) {
                    parameters = queryString.split("&");
                    for (var i = parameters.length - 1; i >= 0; i--) {
                        var parameterKey = parameters[i].split("=")[0];
                        if (parameterKey === key) {
                            parameters[i] = key + "=" + encodeURIComponent(value);
                            keyFound = true;
                        }
                    }
                }

                if (!keyFound) {
                    parameters.push(key + "=" + value);
                }

                var newUrl = baseUrl + "?" + parameters.join("&");

                AppKitchen.BrowserHelper.setUrl(newUrl, options);
            }

            export function removeParameter(key: string, options?: RedirectOptions) {
                options = AppKitchen.OptionsHelper.merge(options, {
                    push: false,
                    refresh: false
                });

                var url = AppKitchen.BrowserHelper.getUrl();
                var newUrl = url.split("?")[0];
                var queryString = (url.indexOf("?") !== -1) ? url.split("?")[1] : "";
                if (queryString !== "") {
                    var parameters = queryString.split("&");
                    for (var i = parameters.length - 1; i >= 0; i--) {
                        var parameterKey = parameters[i].split("=")[0];
                        if (parameterKey === key) {
                            parameters.splice(i, 1);
                        }
                    }
                    if (parameters.length > 0) {
                        newUrl = newUrl + "?" + parameters.join("&");
                    }
                }

                AppKitchen.BrowserHelper.setUrl(newUrl, options);
            }

            export function clear(options?: RedirectOptions) {
                options = AppKitchen.OptionsHelper.merge(options, {
                    push: false,
                    refresh: false
                });

                var url = AppKitchen.BrowserHelper.getUrl();
                AppKitchen.BrowserHelper.setUrl(url.split("?")[0], options);
            }
        }

        export module WindowResize {

            // ReSharper disable once InconsistentNaming
            interface WindowResizeAction {
                action: () => void;
                element?: HTMLElement;
            }

            var counter = 0;
            var actions: { [handle: string]: WindowResizeAction } = {};

            export function on(action: () => void, element?: HTMLElement): number {
                var handle = ++counter;

                actions[handle] = {
                    action: action,
                    element: element
                };

                trigger();
                return handle;
            }

            export function off(handle: number) {
                delete actions[handle];
            }

            export function trigger() {
                if (actions) {
                    var lostActions = [];
                    Object.keys(actions).forEach(key => {
                        var action = actions[key];
                        if (action.element && $(action.element).closest("body").length === 0) {
                            lostActions.push(key);
                        } else {
                            if (action.action) {
                                action.action();
                            }
                        }
                    });

                    // cleanup lost actions
                    if (lostActions.length > 0) {
                        lostActions.forEach(handle => off(handle));
                    }
                }
            }

            $(window).resize(() => AppKitchen.BrowserHelper.WindowResize.trigger());
        }

    }

    export module StringHelper {

        export function getString(key: string, resources: any) {
            if (resources) {
                var string = resources[key];
                if (string) {
                    return string;
                }
            }
            return key;
        }

        export function generateRandom(length: number): string {
            var str = "";
            var possible = "abcdefghijklmnopqrstuvwxyz0123456789";

            for (var i = 0; i < length; i++) {
                str += possible.charAt(Math.floor(Math.random() * possible.length));
            }

            return str;
        }

        export function isValidIdentifier(key: string): boolean {
            // reserved keywords ECMA6
            if (["do", "if", "in", "for", "let", "new", "try", "var", "case", "else",
                "enum", "eval", "null", "this", "true", "void", "with", "break", "catch",
                "class", "const", "false", "super", "throw", "while", "yield", "delete",
                "export", "import", "public", "return", "static", "switch", "typeof",
                "default", "extends", "finally", "package", "private", "continue",
                "debugger", "function", "arguments", "interface", "protected",
                "implements", "instanceof"].indexOf(key) !== -1) {
                return false;
            }

            // illegal special characters
            if (/[\|&;\s\$%@"'<>\(\)\+,\-\\/]/.exec(key) != null) {
                return false;
            }

            // beginning with number
            if (/^[0-9]/.exec(key) != null) {
                return false;
            }

            return true;
        }
    }

    export module OptionsHelper {
        export function merge<TOptions>(customOptions: TOptions, defaultOptions: TOptions, deep?: boolean): TOptions {
            if (customOptions == null && defaultOptions == null) {
                return <TOptions>{};
            }
            if (deep == null) {
                deep = false;
            }
            if (customOptions) {
                return $.extend(deep, {}, defaultOptions, customOptions);
            }
            return defaultOptions;
        }
    }

    export module GuidHelper {
        export function newGuid() {
            return 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx'.replace(/[x]/g, function (c) {
                const r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
                return v.toString(16);
            });
        }
    }

    // ReSharper disable once InconsistentNaming
    export module UIHelper {
        export function renderTemplateTo(container: HTMLElement, template: string, content?: any): JQuery {
            if (content) {
                $(container).html(_.template(template)(content));
            } else {
                $(container).html(template);
            }

            AppKitchen.UIHelper.updateFullHeightGrids(container);

            return $(container);
        }

        export function renderTemplate(template: string, content: any): string {
            return _.template(template)(content);
        }

        export function clearSelection() {
            if ((<any>document).selection) {
                (<any>document).selection.empty();
            } else if (window.getSelection) {
                window.getSelection().removeAllRanges();
            }
        }

        export function updateFullHeightGrids(container?: HTMLElement) {
            var target = $("body");
            if (container) {
                target = $(container).parent();
            }
            target.find(".full-height-grid").each((i, grid) => {
                // set grid height to available height inside parent (excluding padding)
                $(grid).css("height", "");
                var availableHeight = $(grid).parent().height();
                $(grid).outerHeight(availableHeight);
                $(grid).trigger("fill-height");

                // get available height inside grid (exclude padding)
                availableHeight = $(grid).height();
                // substract heights of all other children (include margins)
                $(grid).children().not(".fill-height").each((j, row) => {
                    if ($(row).is(":visible") && $(row).css("position") !== "absolute") {
                        availableHeight -= $(row).outerHeight(true);
                    }
                });

                // set heights of "fill-height" children to the remaining available height
                $(grid).find("> .fill-height").each((j, row) => {
                    $(row).outerHeight(availableHeight);
                    var actualNewHeight = $(row).outerHeight(true);
                    // hack to set element height including margin
                    if (actualNewHeight > availableHeight) {
                        $(row).outerHeight(availableHeight - (actualNewHeight - availableHeight));
                    }
                    $(row).trigger("fill-height");
                });
            });
        }

        AppKitchen.BrowserHelper.WindowResize.on(() => AppKitchen.UIHelper.updateFullHeightGrids());

        export type LoaderType = "mesap" | "circle" | "concentric";

        export type LoaderSize = "S" | "M" | "L" | "XL";

        export interface LoaderRenderOptions {
            size?: LoaderSize,
            fadeIn?: number,
            complete?: () => void,
            type?: LoaderType,
        }

        export interface LoaderRemoveOptions {
            fadeOut?: number,
            complete?: () => void,
        }

        export function renderLoader(target: HTMLElement, options?: LoaderRenderOptions) {
            var opt = AppKitchen.OptionsHelper.merge<LoaderRenderOptions>(options, { size: "M", type: "concentric" });
            
            var template = AppKitchen.Templates.LoadingConcentricSpinner;
            if (opt.type) {
                if (opt.type === "concentric") {
                    template = AppKitchen.Templates.LoadingConcentricSpinner;
                } else if (opt.type === "circle") {
                    template = AppKitchen.Templates.LoadingCircleSpinner;
                } else if (opt.type === "mesap") {
                    template = AppKitchen.Templates.LoadingMesapSpinner;
                }
            }

            $(target).find("> .a-loading").remove();
            $(target).append(AppKitchen.UIHelper.renderTemplate(template, { size: opt.size }));

            if (opt.fadeIn) {
                $(target).find("> .a-loading").fadeIn(opt.fadeIn, opt.complete);
            } else if (opt.complete) {
                opt.complete();
            }
        }

        export function removeLoader(target: HTMLElement, options?: LoaderRemoveOptions) {
            var opt = AppKitchen.OptionsHelper.merge<LoaderRemoveOptions>(options, {});
            var animationContainer = $(target).find("> .a-loading-base");
            if (animationContainer.length > 0) {
                animationContainer.fadeOut(opt.fadeOut || 150, () => {
                    animationContainer.remove();
                    if (opt.complete) {
                        opt.complete();
                    }
                });
            } else if (opt.complete) {
                opt.complete();
            }
        }

        export function renderLoadingAnimation(container: HTMLElement, size: string, fadeIn?: number, complete?: () => void, loaderType?: LoaderType) {
            if (["S", "M", "L", "XL"].indexOf(size) === -1) {
                size = "M";
            }

            $(container).find("> .a-loading").remove();
            if (loaderType && loaderType === "concentric") {
                $(container).append(AppKitchen.UIHelper.renderTemplate(AppKitchen.Templates.LoadingConcentricSpinner, { size: size }));
            } else if (loaderType && loaderType === "circle") {
                $(container).append(AppKitchen.UIHelper.renderTemplate(AppKitchen.Templates.LoadingCircleSpinner, { size: size }));
            } else {
                $(container).append(AppKitchen.UIHelper.renderTemplate(AppKitchen.Templates.LoadingMesapSpinner, { size: size }));
            }

            if (fadeIn) {
                $(container).find("> .a-loading").fadeIn(fadeIn, complete);
            } else if (complete) {
                complete();
            }
        }

        export function removeLoadingAnimation(container: HTMLElement, fadeOut?: number, complete?: () => void) {
            var animationContainer = $(container).find("> .a-loading-base");
            if (animationContainer.length > 0) {
                animationContainer.fadeOut(fadeOut || 150, () => {
                    animationContainer.remove();
                    if (complete) {
                        complete();
                    }
                });
            } else if (complete) {
                complete();
            }
        }

        export function hideTooltips(except?: kendo.ui.Tooltip) {
            $("[data-role=tooltip]").each((i, el) => {
                var tooltip = $(el).data("kendoTooltip");
                if (tooltip && tooltip !== except) {
                    tooltip.hide();
                }
            });
        }

        export function addTooltip(el: JQuery, position: string) {
            el.kendoTooltip({
                    position: position,
                    show: e => UIHelper.hideTooltips(e.sender)
                });
        }

        export function getHighestZIndex(): number {
            var maxZ = -1;
            $("*").each((i, e) => {
                var z = parseInt($(e).css("z-index"), 10);
                if (z && z > maxZ) {
                    maxZ = z;
                }
            });
            return maxZ;
        }

        export module Colors {

            // ReSharper disable once InconsistentNaming
            export interface RgbColor {
                r: number;
                g: number;
                b: number;
            }

            // ReSharper disable once InconsistentNaming
            export interface HslColor {
                h: number;
                s: number;
                l: number;
            }

            // ReSharper disable once InconsistentNaming
            export interface HsvColor {
                h: number;
                s: number;
                v: number;
            }

            export function parseColor(color: string): RgbColor {
                if (color && color.beginsWith("#")) {
                    return hex2Rgb(color);
                }

                if (color && color.beginsWith("rgb")) {
                    return parseRgbString(color);
                }

                throw "Invalid color string";
            }

            export function hex2Rgb(color: string): RgbColor {
                if (color.substr(0, 1) !== "#" || (color.length !== 7 && color.length !== 4))
                    throw "Invalid HEX color code";

                if (color.length === 4) {
                    color = extendShortHex(color);
                }

                var parser = /^#([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(color);

                if (!parser || parser.length !== 4)
                    throw "Invalid HEX color code";

                return {
                    r: parseInt(parser[1], 16),
                    g: parseInt(parser[2], 16),
                    b: parseInt(parser[3], 16)
                }
            }

            export function rgb2Hex(color: RgbColor): string {
                validateRgbComponent("R", color.r);
                validateRgbComponent("G", color.g);
                validateRgbComponent("B", color.b);
                return "#" + componentToHex(color.r) + componentToHex(color.g) + componentToHex(color.b);
            }

            export function extendShortHex(color: string): string {
                var parser = /^#([a-f\d])([a-f\d])([a-f\d])$/i.exec(color);
                if (!parser || parser.length !== 4) {
                    throw "Invalid short HEX color code";
                }
                return "#" + parser[1] + parser[1] + parser[2] + parser[2] + parser[3] + parser[3];
            }

            export function lightenRgb(color: RgbColor, amount: number): RgbColor {
                return {
                    r: Math.floor(color.r + (255 - color.r) * amount),
                    g: Math.floor(color.g + (255 - color.g) * amount),
                    b: Math.floor(color.b + (255 - color.b) * amount)
                }
            }

            export function darkenRgb(color: RgbColor, amount: number): RgbColor {
                return {
                    r: Math.floor(color.r * (1 - amount)),
                    g: Math.floor(color.g * (1 - amount)),
                    b: Math.floor(color.b * (1 - amount))
                }
            }

            export function rgb2rgba(color: RgbColor, a: number): string {
                return "rgba(" + color.r + "," + color.g + "," + color.b + "," + a + ")";
            }

            export function lightenHex(color: string, amount: number): string {
                return rgb2Hex(lightenRgb(hex2Rgb(color), amount));
            }

            export function darkenHex(color: string, amount: number): string {
                return rgb2Hex(darkenRgb(hex2Rgb(color), amount));
            }

            function validateRgbComponent(component: string, value: number): number {
                if (value == null || value < 0 || value > 255) {
                    throw "Invalid " + component + " component";
                }

                return value;
            }

            function parseRgbString(color: string): RgbColor {
                color = color.trim();
                var parser = /^rgb\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/.exec(color);
                if (!parser || parser.length !== 4) {
                    throw "Invalid RGB string";
                }
                return {
                    r: validateRgbComponent("R", parseInt(parser[1])),
                    g: validateRgbComponent("G", parseInt(parser[2])),
                    b: validateRgbComponent("B", parseInt(parser[3]))
                }
            }

            function componentToHex(c) {
                var hex = c.toString(16);
                return hex.length === 1 ? "0" + hex : hex;
            }

        }

        export module Scrolling {

            export function customScroller(targetElement: HTMLElement) {
                if ($(targetElement).hasClass("nano")) {
                    $(targetElement).nanoScroller({ destroy: true });
                    $(targetElement).removeClass("nano");
                    $(targetElement).children().removeClass("nano-content");
                }

                $(targetElement).addClass("nano");
                if ($(targetElement).children().length === 1) {
                    $(targetElement).children().addClass("nano-content");
                } else {
                    $('<div class="nano-content"></div>').appendTo(targetElement).append($(targetElement).children());
                }

                $(targetElement).nanoScroller({ disableResize: false });

                if ($(targetElement).hasClass("full-height-grid") || $(targetElement).hasClass("fill-height")) {
                    $(targetElement).on("fill-height", () => {
                        $(targetElement).nanoScroller({ disableResize: true });
                    });
                }
            }

            export function synchronizeScrolling(scroller1: HTMLElement, scroller2: HTMLElement) {
                var $scroller1 = $(scroller1);
                var $scroller2 = $(scroller2);

                var scrolling1Timeout = null;
                var scrolling2Timeout = null;

                $scroller1.on('scroll', () => {
                    if (!$scroller2.hasClass("scrolling")) {
                        $scroller1.addClass("scrolling");
                        $scroller2.scrollTo($scroller1.scrollTop());
                        clearTimeout(scrolling1Timeout);
                        scrolling1Timeout = setTimeout(() => $scroller1.removeClass("scrolling"), 300);
                    }
                });

                $scroller2.on('scroll', () => {
                    if ($scroller2.scrollTop() !== $scroller1.scrollTop()) {
                        if (!$scroller1.hasClass("scrolling")) {
                            $scroller2.addClass("scrolling");
                            $scroller1.scrollTo($scroller2.scrollTop());
                            clearTimeout(scrolling2Timeout);
                            scrolling2Timeout = setTimeout(() => $scroller2.removeClass("scrolling"), 300);
                        }
                    }
                });
            }

            export function offsetToViewport(element: HTMLElement, scroller: HTMLElement, included?: boolean): number {
                var viewport = {
                    top: $(scroller).scrollTop(),
                    bottom: $(scroller).scrollTop() + $(scroller).height()
                };

                var elementBounds = {
                    top: element.offsetTop,
                    // ReSharper disable once DoubleNegationOfBoolean
                    bottom: element.offsetTop + $(element).outerHeight(!!included)
                }

                if (included) {
                    if (elementBounds.top >= viewport.top && elementBounds.bottom <= viewport.bottom) {
                        return 0;
                    }
                    if (elementBounds.top < viewport.top) {
                        return elementBounds.top - viewport.top;
                    }
                    return elementBounds.bottom - viewport.bottom;
                }

                if (elementBounds.top <= viewport.bottom && elementBounds.bottom >= viewport.top) {
                    return 0;
                }
                if (elementBounds.top > viewport.bottom) {
                    return elementBounds.top - viewport.bottom;
                }
                return elementBounds.bottom - viewport.top;
            }

            export function scrollIntoViewport(element: HTMLElement, scroller: HTMLElement, duration?: number) {
                var distance = Scrolling.offsetToViewport(element, scroller, true);
                if (distance !== 0) {
                    //$(scroller).animate({
                    //    scrollTop: $(scroller).scrollTop() + distance
                    //}, duration | 300);

                    $(scroller).scrollTo($(scroller).scrollTop() + distance, {
                        axis: "y",
                        duration: duration | 300
                    });
                }
            }

        }

        export module Transitions {
            export function introduce(container: HTMLElement, duration: number, action: () => void) {
                $(container).css("opacity", 0);
                action();
                $(container).animate({ opacity: 1 }, duration);
            }

            export function swap(container: HTMLElement, fadeOutDuration: number, fadeInDuration: number, action: () => void, after?: () => void) {
                $(container).animate({ opacity: 0 }, fadeOutDuration, () => {
                    action();
                    $(container).animate({ opacity: 1 }, fadeInDuration, () => {
                        if (after) {
                            after();
                        }
                    });
                });
            }
        }

        export module Windows {

            // ReSharper disable once InconsistentNaming
            export interface WindowButton {
                name: string;
                icon?: string;
                action?: (window: kendo.ui.Window) => void;
            }

            // ReSharper disable once InconsistentNaming
            export interface WindowOptions {
                title?: string;
                buttons?: WindowButton[];
                width?: string;
                height?: string;
                minWidth?: string;
                maxWidth?: string;
                minHeight?: string;
                maxHeight?: string;
                position?: { top: string, left: string };
                easyClose?: boolean;
                modal?: boolean;
                resizable?: boolean;
                draggable?: boolean;
                center?: boolean;
                opened?: (window: kendo.ui.Window) => void;
                closed?: () => void;
                resized?: () => void;
                animate?: boolean;
                keepOnNavigate?: boolean;
                overflow?: boolean;
                windowCssClass?: string;
            }

            export function openWindow(content: HTMLElement, options?: WindowOptions) {
                options = AppKitchen.OptionsHelper.merge(options, {
                    title: undefined,
                    buttons: options && options.title != null ? [{ name: "close" }] : [],
                    width: "80%",
                    height: "80%",
                    maxWidth: "auto",
                    maxHeight: "auto",
                    minHeight: "auto",
                    minWidth: "auto",
                    position: undefined,
                    easyClose: false,
                    modal: false,
                    resizable: false,
                    draggable: options ? !options.modal : false,
                    center: false,
                    animate: true,
                    keepOnNavigate: false,
                    opened: () => { },
                    closed: () => { },
                    resized: () => { },
                    overflow: false
                });

                var modalOverlay;
                var highestZ = AppKitchen.UIHelper.getHighestZIndex();
                if (options.modal) {
                    modalOverlay = $('<div class="a-modal-overlay" style="display:none"></div>').appendTo($("body"));
                    modalOverlay.css("position", "absolute");
                    modalOverlay.css("z-index", highestZ + 1);
                    modalOverlay.fadeIn(options.animate ? 400 : 0);
                }

                var hasTitleBar = options.title || (options.buttons && options.buttons.length > 0);

                var popupWindow: kendo.ui.Window = $(content).kendoWindow({
                    title: hasTitleBar ? options.title || "" : false,
                    draggable: options.draggable,
                    modal: false,
                    scrollable: true,
                    resizable: options.resizable,
                    actions: [],
                    deactivate: () => {
                        if (options.closed) options.closed();
                        popupWindow.destroy();
                    },
                    close: () => {
                        if (options.modal) {
                            modalOverlay.fadeOut(options.animate ? 300 : 0, () => modalOverlay.remove());
                        }
                    },
                    visible: false,
                    animation: {
                        open: options.animate === false ? false : undefined,
                        close: options.animate === false ? false : undefined
                    },
                    activate: e => {
                        if (options.opened) {
                            options.opened(popupWindow);
                        }
                        AppKitchen.UIHelper.updateFullHeightGrids(e.sender.wrapper[0]);
                        // easyClose on overlay click (register after animation end)
                        if (options.easyClose) {
                            if (options.modal) {
                                modalOverlay.click(() => e.sender.close());
                            }
                        }
                    }
                }).data("kendoWindow");
                popupWindow.bind("close",
                    () => {
                        $("body").find(".a-main").removeClass("is-background");
                    });
                $("body").find(".a-main").addClass("is-background");

                // css properties
                popupWindow.wrapper.css("position", "absolute");
                popupWindow.wrapper.css("z-index", highestZ + 2);
                popupWindow.wrapper.css("width", options.width);
                popupWindow.wrapper.css("height", options.height);
                popupWindow.wrapper.css("min-width", options.minWidth);
                popupWindow.wrapper.css("max-width", options.maxWidth);
                popupWindow.wrapper.css("min-height", options.minHeight);
                popupWindow.wrapper.css("max-height", options.maxHeight);
                if (options.overflow) {
                    popupWindow.wrapper.find(".k-window-content").addClass("overflow");
                }
                if (options.position) {
                    popupWindow.wrapper.css("top", options.position.top);
                    popupWindow.wrapper.css("left", options.position.left);
                } else {
                    popupWindow.center();
                }

                // center
                if (options.center || (options.modal && !options.position)) {
                    popupWindow.center();
                    AppKitchen.BrowserHelper.WindowResize.on(() => popupWindow.center(), popupWindow.wrapper[0]);
                }

                if (options.windowCssClass) {
                    popupWindow.wrapper.addClass(options.windowCssClass);
                }


                popupWindow.open();

                // easy close on ESC
                if (options.easyClose) {
                    popupWindow.wrapper.keydown((e) => {
                        if (e.which === 27) {
                            popupWindow.close();
                        }
                    });
                }

                // update full height grids & responsive on resize
                if (options.resizable) {
                    popupWindow.bind("resize", () => {
                        AppKitchen.UIHelper.Responsive.update();
                        AppKitchen.UIHelper.updateFullHeightGrids(popupWindow.wrapper[0]);
                        if (options.resized) {
                            options.resized();
                        }
                    });
                }

                // close window on navigate
                if (!options.keepOnNavigate) {
                    $("body").on("navigate", () => popupWindow.close());
                }

                // add buttons
                if (options.buttons) {
                    var buttonsContainer = popupWindow.wrapper.find(".k-window-actions");
                    options.buttons.forEach(button => {

                        var $btn = $(AppKitchen.UIHelper.renderTemplate(AppKitchen.Templates.WindowButton, {
                            name: button.name,
                            icon: button.icon || button.name
                        })).prependTo(buttonsContainer);

                        if (button.action) {
                            $btn.click(button.action.bind(this, popupWindow));
                        } else {
                            // default actions
                            switch (button.name) {
                                case "close":
                                    $btn.click(() => popupWindow.close());
                                    break;
                                case "maximize":
                                    $btn.click(() => popupWindow.maximize());
                                    break;
                                case "minimize":
                                    $btn.click(() => popupWindow.minimize());
                                    break;
                                case "restore":
                                    $btn.click(() => popupWindow.restore());
                                    break;
                            }
                        }
                    });
                }

                return popupWindow;
            }
        }

        export module DialogBoxes {

            export type DialogBoxIcon = "ok" | "warning" | "fail" | "unknown";

            // ReSharper disable once InconsistentNaming
            export interface DialogBoxOptions {
                title?: string | boolean;
                icon?: DialogBoxIcon;
                cssClass?: string;
            }

            // ReSharper disable once InconsistentNaming
            export interface AlertDialogOptions extends DialogBoxOptions {
                label?: string;
                close?: () => void;
            }

            export function alert(content: string, dialogOptions?: AlertDialogOptions) {
                var options = AppKitchen.OptionsHelper.merge(dialogOptions, {
                    title: false,
                    label: AppKitchen.Strings.Ok,
                    close: () => { },
                    icon: null
                });

                $("body > .a-dialog-box").remove();
                $("body").append('<div class="a-dialog-box"></div>');
                var container = $("body > .a-dialog-box")[0];

                AppKitchen.UIHelper.renderTemplateTo(container, AppKitchen.Templates.DialogBoxAlert, {
                    icon: options.icon,
                    title: options.title,
                    content: content,
                    ok: options.label
                });

                var box = AppKitchen.UIHelper.Windows.openWindow(container, {
                    width: "30em",
                    height: "auto",
                    center: true,
                    easyClose: false,
                    modal: true,
                    animate: false,
                    buttons: [],
                    windowCssClass: options.cssClass
                });

                $(container).find(".a-alert-btn-ok").click(() => {
                    box.close();
                    options.close();
                });
            }

            // ReSharper disable once InconsistentNaming
            export interface ConfirmDialogOptions extends DialogBoxOptions {
                strings?: { ok: string, cancel: string };
                cancel?: () => void;
            }

            export function confirm(content: string, confirm: () => void, dialogOptions?: ConfirmDialogOptions) {
                var options = AppKitchen.OptionsHelper.merge(dialogOptions, {
                    title: false,
                    strings: { ok: AppKitchen.Strings.Ok, cancel: AppKitchen.Strings.Cancel },
                    cancel: () => { },
                    icon: null
                });

                $("body > .a-dialog-box").remove();
                $("body").append('<div class="a-dialog-box"></div>');
                var container = $("body > .a-dialog-box")[0];

                AppKitchen.UIHelper.renderTemplateTo(container, AppKitchen.Templates.DialogBoxConfirm, {
                    icon: options.icon,
                    title: options.title,
                    content: content,
                    ok: options.strings.ok,
                    cancel: options.strings.cancel
                });

                var box = AppKitchen.UIHelper.Windows.openWindow(container, {
                    width: "30em",
                    height: "auto",
                    center: true,
                    easyClose: false,
                    modal: true,
                    animate: false,
                    buttons: [],
                    windowCssClass: options.cssClass
                });

                $(container).find(".a-confirm-btn-ok").click(() => {
                    box.close();
                    confirm();
                });

                $(container).find(".a-confirm-btn-cancel").click(() => {
                    box.close();
                    options.cancel();
                });
            }

            export function yesNo(content: string, yes: () => void, no?: () => void, title?: string) {
                return AppKitchen.UIHelper.DialogBoxes.confirm(content, yes, {
                    title: title || "",
                    strings: { ok: AppKitchen.Strings.Yes, cancel: AppKitchen.Strings.No },
                    cancel: () => { if (no) no(); }
                });
            }

        }

        export module Notifications {
            var defaultNotificationStack: HTMLElement;

            export function notify(content: string, type: AppKitchen.Controls.Popups.NotificationType, options?: AppKitchen.Controls.Popups.NotificationOptions): AppKitchen.Controls.Popups.Notification {
                return new AppKitchen.Controls.Popups.Notification(content, AppKitchen.OptionsHelper.merge<AppKitchen.Controls.Popups.NotificationOptions>(options, {
                    type: type,
                    stack: getNotificationStack()
                }));
            }

            export function closeAll() {
                if (defaultNotificationStack) {
                    $(defaultNotificationStack).children().fadeOut(150, () => {
                        $(defaultNotificationStack).empty();
                    });
                }
            }

            function getNotificationStack(): HTMLElement {
                // reuse existing notification stack
                if (!defaultNotificationStack) {
                    defaultNotificationStack = $(AppKitchen.Templates.DefaultNotificationStack).appendTo("body")[0];
                }

                // bring notification stack to front
                $(defaultNotificationStack).css("z-index", AppKitchen.UIHelper.getHighestZIndex() + 1);

                return defaultNotificationStack;
            }
        }

        export module Responsive {
            var targets: { container: HTMLElement; minWidth: number; cssClass: string }[] = [];

            export function update() {
                if (targets) {
                    var lostTargets = [];
                    targets.forEach(target => {
                        if ($(target.container).closest("body").length === 0) {
                            lostTargets.push(target);
                        } else if ($(target.container).width() <= target.minWidth) {
                            $(target.container).addClass(target.cssClass);
                        } else {
                            $(target.container).removeClass(target.cssClass);
                        }
                    });

                    // cleanup targets
                    if (lostTargets.length > 0) {
                        lostTargets.forEach(target => {
                            var i = targets.indexOf(target);
                            targets.splice(i, 1);
                        });
                    }
                }
            }

            export function registerTarget(container: HTMLElement, minWidth: number, cssClass: string) {
                targets.push({
                    container: container,
                    minWidth: minWidth,
                    cssClass: cssClass
                });

                update();
            }

            AppKitchen.BrowserHelper.WindowResize.on(() => AppKitchen.UIHelper.Responsive.update());
        }

    }

    export module Colors {
        export var CoalGray: string;
        export var DarkGray: string;
        export var Gray: string;
        export var MediumGray: string;
        export var Silver: string;
        export var LightGray: string;
        export var Gainsboro: string;
        export var WhiteSmoke: string;
        export var Orange: string;
        export var DarkOrange: string;
        export var OrangeRed: string;
        export var Red: string;
        export var Green: string;
        export var LightBlue: string;
        export var DarkBlue: string;
        export var CiColor: string;
        export var CiColorForeground: string;
        export var CiColor2: string;
        export var CiColor2Foreground: string;
    }

    export module LocalStorageHelper {
        export function store(key: string, value: any, options?: any) {
            if (options && options.expires) {
                var storedObject = {
                    value: value,
                    expires: kendo.toString(new Date().addDays(options.expires), "yyyyMMdd HH:mm")
                }
                localStorage.setItem(key, JSON.stringify(storedObject));
            } else {
                localStorage.setItem(key, value.toString());
            }
        }

        export function read(key: string) {
            var storedString = localStorage.getItem(key);
            var storedObject = JSON.parse(storedString);
            if (storedObject && storedObject.value) {
                return storedObject.value;
            }
            return storedString;
        }

        export function cleanUp() {
            // collect expired items
            var expired: string[] = [];
            $.each(localStorage, (key, storedString) => {
                try {
                    var storedObject = JSON.parse(storedString);
                    if (storedObject && storedObject.expires) {
                        var expirationDate = kendo.parseDate(storedObject.expires, "yyyyMMdd HH:mm");
                        if (new Date() >= expirationDate) {
                            expired.push(key);
                        }
                    }
                } catch (exception) {

                }
            });
            // remove them
            for (let i = 0; i < expired.length; i++) {
                localStorage.removeItem(expired[i]);
            }
        }

        interface LocalStorageListener {
            key: string;
            callback: (value: string) => void;
        }

        var listeners: LocalStorageListener[] = [];

        function storageEventHandler(e) {
            for (let i = 0; i < listeners.length; i++) {
                if (e.key === listeners[i].key) {
                    listeners[i].callback(read(listeners[i].key));
                }
            }
        }

        export function registerListener(key: string, callback: (value: string) => void) {
            listeners.push({
                key: key,
                callback: callback
            });
        }

        cleanUp();
        // ReSharper disable once Html.EventNotResolved
        window.addEventListener("storage", (e) => storageEventHandler(e), false);
    }

    export module ExportHelper {
        // ReSharper disable once InconsistentNaming
        export interface ExportChartDataToExcelDefinition {
            timeseriesId: string;
            header: string;
            color: string;
        }

        export function exportChartDataToExcel(chartsData: AppKitchen.Data.TimeSeries.Model.ChartData[],
            definitions: ExportChartDataToExcelDefinition[],
            sheetTitle: string,
            filename: string) {
            var exportChartsData = [];
            definitions.forEach(def => {
                var chartData = chartsData.AsLinq<AppKitchen.Data.TimeSeries.Model.ChartData>()
                    .FirstOrDefault(cd => cd.timeseriesId === def.timeseriesId);
                if (chartsData) {
                    exportChartsData.push({
                        data: chartData.data,
                        header: def.header,
                        color: def.color
                    });
                }
            });

            var excelData = [];
            var firstChartData = exportChartsData[0];
            for (let dataIndex = 0; dataIndex < firstChartData.data.length; dataIndex++) {
                var tsData = firstChartData.data[dataIndex];
                var rowData = {
                    timepoint: new Date(tsData[0]),
                    "#1": tsData[1]
                }

                if (exportChartsData.length > 1) {
                    for (let tsIndex = 1; tsIndex < exportChartsData.length; tsIndex++) {
                        rowData["#" + (tsIndex + 1)] = exportChartsData[tsIndex].data[dataIndex][1];
                    }
                }

                excelData.push(rowData);
            }

            var columnDefinitions: Data.Export.ExcelExportColumnDefinition[] = [
                {
                    header: AppKitchen.Strings.ExcelExport_DateTimeColumnHeader,
                    field: "timepoint",
                    format: "dd.mm.yyyy hh:mm",
                    width: 150,
                    styles: {
                        headerBorder: <any>{ color: Colors.LightGray, size: 3 },
                        headerBackground: "#FFFFFF",
                        headerForeground: Colors.Gray
                    }
                }
            ];

            for (let tsIndex = 0; tsIndex < exportChartsData.length; tsIndex++) {
                columnDefinitions.push({
                    header: exportChartsData[tsIndex].header.toUpperCase(),
                    field: "#" + (tsIndex + 1),
                    width: 150,
                    styles: {
                        headerBorder: <any>{ color: exportChartsData[tsIndex].color, size: 3 },
                        headerBackground: "#FFFFFF",
                        headerForeground: exportChartsData[tsIndex].color,
                        background: exportChartsData[tsIndex].color ? UIHelper.Colors.lightenHex(exportChartsData[tsIndex].color, .9) : undefined
                    }
                });
            }

            Data.Export.saveAsExcel(excelData, columnDefinitions, sheetTitle, filename);
        }

        // ReSharper disable once InconsistentNaming
        export interface ExportChartDataToCsvDefinition {
            timeseriesId: string;
            header: string;
        }

        export function exportChartDataToCsv(chartsData: AppKitchen.Data.TimeSeries.Model.ChartData[],
            definitions: ExportChartDataToCsvDefinition[],
            filename: string,
            dateFormat?: string,
            options?: Data.Export.CsvExportOptions) {
            var exportChartsData = [];
            definitions.forEach(def => {
                var chartData = chartsData.AsLinq<AppKitchen.Data.TimeSeries.Model.ChartData>()
                    .FirstOrDefault(cd => cd.timeseriesId === def.timeseriesId);
                if (chartsData) {
                    exportChartsData.push({
                        data: chartData.data,
                        header: def.header
                    });
                }
            });

            var csvData = [];
            var firstChartData = exportChartsData[0];
            for (let dataIndex = 0; dataIndex < firstChartData.data.length; dataIndex++) {
                var tsData = firstChartData.data[dataIndex];
                var rowData = {
                    timepoint: new Date(tsData[0]),
                    "#1": tsData[1]
                }

                if (exportChartsData.length > 1) {
                    for (let tsIndex = 1; tsIndex < exportChartsData.length; tsIndex++) {
                        rowData["#" + (tsIndex + 1)] = exportChartsData[tsIndex].data[dataIndex][1];
                    }
                }

                csvData.push(rowData);
            }

            var columnDefinitions: Data.Export.CsvExportColumnDefinition[] = [
                {
                    header: AppKitchen.Strings.ExcelExport_DateTimeColumnHeader,
                    field: "timepoint",
                    format: dateFormat || "g"
                }
            ];

            for (let tsIndex = 0; tsIndex < exportChartsData.length; tsIndex++) {
                columnDefinitions.push({
                    header: exportChartsData[tsIndex].header.toUpperCase(),
                    field: "#" + (tsIndex + 1)
                });
            }

            Data.Export.saveAsCsv(csvData, columnDefinitions, filename, options);
        }
    }

    // Moved to AppKitchen.Controls.Tile
    //export module Tile {

    //    export enum TileStyle {
    //        Dark,
    //        Bright
    //    }

    //    export enum TileSize {
    //        L,
    //        M
    //    }

    //    // ReSharper disable once InconsistentNaming
    //    export interface TileOptions {
    //        style?: TileStyle,
    //        size?: TileSize;
    //        id: string,
    //        icon?: string,
    //        image?: string,
    //        title: string,
    //        subtitle?: string,
    //        status?: string,
    //        statusIcon?: string,
    //        click?: (id: string) => void,
    //    }

    //    export function create(target: HTMLElement, options: TileOptions) {
    //        UIHelper.renderTemplateTo(target,
    //            AppKitchen.Templates.Tile,
    //            {
    //                style: TileStyle[options.style],
    //                size: TileSize[options.size],
    //                id: options.id,
    //                icon: options.icon,
    //                image: options.image,
    //                title: options.title,
    //                subtitle: options.subtitle,
    //                status: options.status,
    //                statusIcon: options.statusIcon
    //            });
    //        $(target).find(`a-tile[data-id=${options.id}]`).bind("click", () => options.click(options.id));
    //    }

    //    export function append(target: HTMLElement, options: TileOptions) {
    //        options = AppKitchen.OptionsHelper.merge<TileOptions>(options,
    //            {
    //                id: options.id ? options.id : GuidHelper.newGuid(),
    //                title: null,
    //                style: TileStyle.Bright,
    //                size: TileSize.M
    //            });
    //        var rendertedTemplate = UIHelper.renderTemplate(AppKitchen.Templates.Tile,
    //            {
    //                style: TileStyle[options.style],
    //                size: TileSize[options.size],
    //                id: options.id,
    //                icon: options.icon,
    //                image: options.image,
    //                title: options.title,
    //                subtitle: options.subtitle,
    //                status: options.status,
    //                statusIcon: options.statusIcon
    //            });
    //        $(rendertedTemplate).appendTo(target);
    //        $(target).find(`a-tile[data-id=${options.id}]`).bind("click", () => options.click(options.id));
    //    }
    //}

}

interface Date {
    addDays(days: number): Date;
    addHours(hours: number): Date;
    addMinutes(minutes: number): Date;
    onlyDate(): Date;
    toLocalISOString(): string;
}

interface String {
    truncate(length: number): string;
    nl2br(): string;
    endsWith(searchString: string): boolean;
    beginsWith(searchString: string): boolean;
    escapeHTML(): string;
    toDate(): Date;
    replaceAll(searchValue: string, replaceValue: string): string;
}

String.prototype.toDate = function () {
    return kendo.parseDate(this, "yyyy-MM-ddTHH:mm:ss");
}

Date.prototype.addDays = function (days: number): Date {
    var oldDate = new Date(this.valueOf());
    var newDate = new Date(this.valueOf() + days * 86400000);
    return new Date(newDate.valueOf() + (newDate.getTimezoneOffset() - oldDate.getTimezoneOffset()) * 60000);
};

Date.prototype.addHours = function (hours: number): Date {
    return new Date(this.valueOf() + hours * 3600000);
};

Date.prototype.addMinutes = function (minutes: number): Date {
    return new Date(this.valueOf() + minutes * 60000);
};

Date.prototype.onlyDate = function (): Date {
    var date = new Date(this.valueOf());
    date.setHours(0, 0, 0, 0);
    return date;
};

Date.prototype.toLocalISOString = function (): string {
    return new Date(this.valueOf() - this.getTimezoneOffset() * 60000).toISOString().slice(0, -1);
};

Date.prototype.toString = function (): string {
    return this.toISOString();
};


// do not change signature! This is used in Portal as well!
(<any>String).format = function () {
    var s = arguments[0];
    for (var i = 0; i < arguments.length - 1; i++) {
        var reg = new RegExp("\\{" + i + "\\}", "gm");
        s = s.replace(reg, arguments[i + 1]);
    }

    return s;
};

String.prototype.endsWith = function (searchString) {
    var subjectString = this.toString();
    var position = subjectString.length - searchString.length;
    var lastIndex = subjectString.indexOf(searchString, position);
    return lastIndex === position;
};

String.prototype.beginsWith = function (searchString) {
    var subjectString = this.toString();
    return subjectString.substr(0, searchString.length) === searchString;
};

String.prototype.truncate = function (length): string {
    if (length === 0) return "...";
    if (this.length > length) {
        return this.substr(0, length).trim() + " ...";
    }
    return this;
}

String.prototype.nl2br = function (): string {
    return this.replace(/([^>\r\n]?)(\r\n|\n\r|\r|\n)/g, '$1' + '<br>');
}

String.prototype.escapeHTML = function (): string {
    return this.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

String.prototype.replaceAll = function (searchValue, replaceValue): string {
    var str = this;
    return str.replace(new RegExp(searchValue, "g"), replaceValue);
}

function updateColors() {
    if ($("body").length === 0)
        return;

    var allColors: string[] = [
        "CoalGray",
        "DarkGray",
        "Gray",
        "MediumGray",
        "Silver",
        "LightGray",
        "Gainsboro",
        "WhiteSmoke",
        "Orange",
        "DarkOrange",
        "OrangeRed",
        "Red",
        "Green",
        "LightBlue",
        "DarkBlue",
        "CiColor",
        "CiColorForeground",
        "CiColor2",
        "CiColor2Foreground"
    ];
    allColors.AsLinq<string>().ForEach(color => {
        var dummy = $("<div class='a-color-dummy-" + color + "'></div>");
        $("body").append(dummy);
        var c = AppKitchen.UIHelper.Colors.parseColor(dummy.css("color"));
        AppKitchen.Colors[color] = AppKitchen.UIHelper.Colors.rgb2Hex(c);
        dummy.remove();
    });
}

updateColors();

function compareSortNr(nr1: any, nr2: any) {
    var a = Number.POSITIVE_INFINITY;
    var b = Number.POSITIVE_INFINITY;
    if (nr1) {
        a = parseInt(nr1);
    }
    if (nr2) {
        b = parseInt(nr2);
    }
    return a > b ? 1 : -1;
}
